import React, { useState, useEffect } from "react";
import { httpService } from "../../services/httpService";
import { FaFileInvoice, FaPlus } from "react-icons/fa";
import DynamicTable from "../../components/common/DynamicTable";
import SupplierAddCredit from "./SupplierAddCredit";

const SupplierCreditNote = ({ supplierId, supplierName }) => {
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCreditDrawer, setShowAddCreditDrawer] = useState(false);

  useEffect(() => {
    if (supplierId) {
      fetchCreditNotes();
    }
  }, [supplierId]);

  const fetchCreditNotes = async () => {
    setLoading(true);
    try {
      const response = await httpService.get(
        `/vendorsuppliercredit/getVendorSupplierCredits/${supplierId}`
      );
      if (response.status && response.code === 200) {
        setCreditNotes(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching credit notes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreditAdded = (data) => {
    // Refresh the list
    fetchCreditNotes();
    // Close the drawer
    setShowAddCreditDrawer(false);
  };

  const columns = [
    {
      key: "credit_note_date",
      header: "Date",
      sortable: true,
      renderCell: row => (
        <span>{row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-IN') : "N/A"}</span>
      )
    },
    {
      key: "return_date",
      header: "Return Date",
      sortable: true,
      renderCell: row => (
        <span>{row.return_date ? new Date(row.return_date).toLocaleDateString('en-IN') : "-"}</span>
      )
    },
    {
      key: "credit_note_type",
      header: "Type",
      sortable: true,
      renderCell: row => (
        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
          row.credit_note_type === "return" 
            ? "bg-blue-100 text-blue-800" 
            : "bg-purple-100 text-purple-800"
        }`}>
          {row.credit_note_type === "return" ? "Return Items" : "Other"}
        </span>
      )
    },
    {
      key: "credit_amount",
      header: "Amount",
      sortable: true,
      renderCell: row => (
        <span className="font-semibold text-green-600">
          ₹{(row.credit_amount || 0).toFixed(2)}
        </span>
      )
    },
    {
      key: "used_amount",
      header: "Used Amount",
      sortable: true,
      renderCell: row => (
        <span className="text-orange-600">
          ₹{(row.used_amount || 0).toFixed(2)}
        </span>
      )
    },
    {
      key: "remaining_amount",
      header: "Remaining",
      sortable: true,
      renderCell: row => (
        <span className="font-semibold text-blue-600">
          ₹{(row.remaining_amount || 0).toFixed(2)}
        </span>
      )
    },
    {
      key: "credit_reason",
      header: "Reason",
      sortable: false,
      renderCell: row => (
        <span className="text-gray-600">{row.credit_reason || "-"}</span>
      )
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      renderCell: row => {
        const status = row.status || "pending";
        let statusClass = "";
        let statusText = "";

        switch(status) {
          case "Redeemed":
            statusClass = "bg-gray-100 text-gray-800";
            statusText = "Redeemed";
            break;
          case "Partial Redeemed":
            statusClass = "bg-yellow-100 text-yellow-800";
            statusText = "Partial Redeemed";
            break;
          case "Active":
            statusClass = "bg-green-100 text-green-800";
            statusText = "Active";
            break;
          default:
            statusClass = "bg-gray-100 text-gray-800";
            statusText = status;
        }

        return (
          <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${statusClass}`}>
            {statusText}
          </span>
        );
      }
    }
  ];

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-500">
        Loading credit notes...
      </div>
    );
  }

  return (
    <>
      <div className="bg-white p-4 min-h-[400px]">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-slate-700">
            Credit Notes History
          </h3>
          <button
            onClick={() => setShowAddCreditDrawer(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-full shadow"
          >
            + Credit Note
          </button>
        </div>

        <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
          {creditNotes.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <FaFileInvoice className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No credit notes found for this supplier</p>
            </div>
          ) : (
            <DynamicTable
              columns={columns}
              data={creditNotes}
              showSearch={true}
              showPagination={true}
              showPageInfo={true}
              initialPageSize={10}
              emptyMessage="No credit notes found"
            />
          )}
        </div>
      </div>

      {/* Reuse the SupplierAddCredit drawer */}
      <SupplierAddCredit
        isOpen={showAddCreditDrawer}
        onClose={() => {
          setShowAddCreditDrawer(false);
        }}
        onSave={handleCreditAdded}
        supplierId={supplierId}
        supplierReturnId={null}
      />
    </>
  );
};

export default SupplierCreditNote;